/**
 * AutonomousActivityIndicator — Floating "Felix is building" widget.
 *
 * Shows live progress of an autonomous goal-mode run in a non-modal,
 * collapsible panel docked top-right. Implements the Wave 10C UX
 * contract:
 *
 * - Non-blocking: chat and navigation stay interactive.
 * - Progressive reveal: entities appear as created, ordered by wave
 *   + position, with smooth fade-in. Honors prefers-reduced-motion.
 * - Follow Mode aware: a toggle in this widget mirrors the one in
 *   Felix chat; flipping either updates the shared store.
 * - Auto-dismisses 8s after a terminal event (completed / failed);
 *   user can dismiss earlier or pin it open by expanding.
 *
 * Hidden entirely when there is no active run AND no recent terminal
 * run to summarize.
 */

import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  X,
  Loader2,
  CheckCircle2,
  XCircle,
  Ban,
  Eye,
  EyeOff,
} from "lucide-react";

import {
  useAutonomousRunStore,
  entityTypeToListPath,
  type AutonomousEntityCreated,
} from "@/stores/autonomousRun";
import { assistantApi } from "@/api/services";
import { cn } from "@/lib/utils";
import { useTemplateLabels } from "@/hooks/useTemplateLabels";

// After a run ends, keep the summary visible this long before auto-
// dismissing. Long enough to read, short enough to disappear.
const AUTO_DISMISS_MS = 8000;

function formatElapsed(ms: number | undefined): string {
  if (!ms || ms < 0) return "";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60_000);
  const secs = Math.round((ms % 60_000) / 1000);
  return `${mins}m ${secs}s`;
}

function formatEntityType(t: string): string {
  return (
    {
      objective: "objective",
      user_story: "user story",
      impl_task: "task",
      task: "task",
      test_case: "test case",
      component: "component",
      defect: "defect",
      sprint: "sprint",
      document: "document",
    }[t] ?? t.replace(/_/g, " ")
  );
}

export function AutonomousActivityIndicator() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const reducedMotion = useReducedMotion();
  // Template-layer SKIN of the per-type group heading. The pre-skin word this
  // indicator shows today (lowercase humanized slug, naively pluralized with a
  // trailing "s") is passed as the per-surface fallback so a non-template
  // project is byte-identical; a marketing project reads the manifest plural.
  const { label: skinLabel } = useTemplateLabels(projectId ?? null);

  const activeRun = useAutonomousRunStore((s) => s.activeRun);
  const followMode = useAutonomousRunStore((s) => s.followMode);
  const setFollowMode = useAutonomousRunStore((s) => s.setFollowMode);
  const indicatorExpanded = useAutonomousRunStore((s) => s.indicatorExpanded);
  const setIndicatorExpanded = useAutonomousRunStore(
    (s) => s.setIndicatorExpanded
  );
  const dismissRun = useAutonomousRunStore((s) => s.dismissRun);
  // Wave 10D — cooperative cancel
  const cancelInFlight = useAutonomousRunStore((s) => s.cancelInFlight);
  const setCancelInFlight = useAutonomousRunStore((s) => s.setCancelInFlight);

  const handleCancel = async () => {
    if (!activeRun || activeRun.status !== "running" || cancelInFlight) return;
    setCancelInFlight(true);
    try {
      await assistantApi.cancelGoalRun(activeRun.goalRunId);
      // We do NOT flip store status here — the SSE
      // autonomous.goal_cancelled event (emitted when the loop
      // observes the flag and finishes the in-flight wave) is the
      // authoritative transition. The spinner stays visible until
      // SSE confirms, giving honest feedback that in-flight work
      // is still completing.
    } catch {
      // Surface failure by clearing the in-flight flag; the run
      // continues. The user can retry.
      setCancelInFlight(false);
    }
  };

  // Auto-dismiss after terminal event
  useEffect(() => {
    if (!activeRun) return;
    if (activeRun.status === "running") return;
    const t = setTimeout(() => dismissRun(), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [activeRun, dismissRun]);

  // Follow Mode auto-navigation. When a new entity lands and
  // followMode is ON, navigate to its list page. Only navigates
  // once per entity_type per wave so we don't thrash across
  // multiple creates of the same type. A ref (not state) holds the
  // last-navigated key — updating it shouldn't cause a re-render.
  const lastNavKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!followMode || !activeRun || !projectId) return;
    const newest = activeRun.entities[activeRun.entities.length - 1];
    if (!newest) return;
    const navKey = `${newest.waveNumber}:${newest.entityType}`;
    if (navKey === lastNavKeyRef.current) return;
    const path = entityTypeToListPath(projectId, newest.entityType);
    if (!path) return;
    lastNavKeyRef.current = navKey;
    navigate(path);
  }, [activeRun, followMode, projectId, navigate]);

  const entitiesByType = useMemo(() => {
    if (!activeRun) return new Map<string, AutonomousEntityCreated[]>();
    const m = new Map<string, AutonomousEntityCreated[]>();
    for (const e of activeRun.entities) {
      const arr = m.get(e.entityType) ?? [];
      arr.push(e);
      m.set(e.entityType, arr);
    }
    return m;
  }, [activeRun]);

  if (!activeRun) return null;

  const running = activeRun.status === "running";
  const succeeded = activeRun.status === "completed";
  const failed = activeRun.status === "failed";
  const cancelled = activeRun.status === "cancelled";

  const totalEntities =
    activeRun.totalEntitiesCreated ?? activeRun.entities.length;
  const currentWave = activeRun.waves[activeRun.waves.length - 1];

  const StatusIcon = running
    ? Loader2
    : succeeded
      ? CheckCircle2
      : cancelled
        ? Ban
        : failed
          ? XCircle
          : Loader2;

  const statusColor = running
    ? "text-blue-600 dark:text-blue-400"
    : succeeded
      ? "text-green-600 dark:text-green-400"
      : cancelled
        ? "text-amber-600 dark:text-amber-400"
        : failed
          ? "text-red-600 dark:text-red-400"
          : "text-muted-foreground";

  const headline = running
    ? cancelInFlight
      ? `Cancelling — finishing in-flight wave…`
      : currentWave
        ? `Felix is building — wave ${currentWave.waveNumber} (${activeRun.entities.length} created)`
        : "Felix is starting…"
    : succeeded
      ? `Done — ${totalEntities} entities created in ${formatElapsed(activeRun.elapsedMs)}`
      : cancelled
        ? `Cancelled — ${totalEntities} created before stop`
        : failed
          ? `Stopped — ${totalEntities} created before ${activeRun.stopReason ?? "error"}`
          : "Felix is working";

  return (
    <div
      className="fixed top-4 right-4 z-50 max-w-sm w-80 pointer-events-auto"
      data-testid="autonomous-activity-indicator"
      role="status"
      aria-live="polite"
      aria-label={headline}
    >
      <motion.div
        initial={reducedMotion ? false : { opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
        transition={{ duration: reducedMotion ? 0 : 0.25 }}
        className={cn(
          "rounded-lg border shadow-lg backdrop-blur-sm",
          "bg-white dark:bg-slate-900/95 border-slate-200 dark:border-slate-700",
          "overflow-hidden"
        )}
      >
        {/* Header row — click to expand */}
        <button
          type="button"
          onClick={() => setIndicatorExpanded(!indicatorExpanded)}
          className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors"
        >
          <StatusIcon
            className={cn(
              "h-4 w-4 shrink-0",
              statusColor,
              running && !reducedMotion && "animate-spin"
            )}
          />
          <span className="flex-1 text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
            {headline}
          </span>
          {indicatorExpanded ? (
            <ChevronUp className="h-4 w-4 text-slate-500 dark:text-slate-400 shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-500 dark:text-slate-400 shrink-0" />
          )}
        </button>

        <AnimatePresence initial={false}>
          {indicatorExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: reducedMotion ? 0 : 0.2 }}
              className="overflow-hidden"
            >
              <div className="border-t border-slate-200 dark:border-slate-700 px-3 py-2 space-y-2 max-h-96 overflow-y-auto">
                {/* Follow Mode toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                    {followMode ? (
                      <Eye className="h-3.5 w-3.5" />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5" />
                    )}
                    <span>Follow Mode</span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFollowMode(!followMode);
                    }}
                    className={cn(
                      "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                      followMode ? "bg-blue-600" : "bg-slate-100 dark:bg-slate-800"
                    )}
                    aria-pressed={followMode}
                    aria-label={
                      followMode ? "Turn Follow Mode off" : "Turn Follow Mode on"
                    }
                  >
                    <span
                      className={cn(
                        "inline-block h-4 w-4 rounded-full bg-white shadow transition-transform",
                        followMode ? "translate-x-4" : "translate-x-0.5"
                      )}
                    />
                  </button>
                </div>

                {/* Waves summary */}
                {activeRun.waves.length > 0 && (
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {activeRun.waves.length}{" "}
                    {activeRun.waves.length === 1 ? "wave" : "waves"} ·{" "}
                    {activeRun.entities.length} entities
                  </div>
                )}

                {/* Entities grouped by type */}
                {entitiesByType.size > 0 && (
                  <div className="space-y-2">
                    {Array.from(entitiesByType.entries()).map(([type, list]) => (
                      <div key={type}>
                        <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                          {skinLabel(type, {
                            plural: list.length > 1,
                            fallback:
                              formatEntityType(type) +
                              (list.length > 1 ? "s" : ""),
                          })}{" "}
                          ({list.length})
                        </div>
                        <ul className="space-y-0.5">
                          {list.slice(-5).map((e) => (
                            <li
                              key={e.entityId}
                              className="flex items-center gap-1.5 text-xs"
                            >
                              <span
                                className={cn(
                                  "h-1 w-1 rounded-full shrink-0",
                                  e.action === "create"
                                    ? "bg-green-500"
                                    : "bg-blue-500"
                                )}
                              />
                              <span className="flex-1 truncate text-slate-900 dark:text-slate-100">
                                {e.title || e.entityId}
                              </span>
                            </li>
                          ))}
                          {list.length > 5 && (
                            <li className="text-xs text-slate-500 dark:text-slate-400 pl-3">
                              + {list.length - 5} more
                            </li>
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {/* Cancel button while running, Dismiss when terminal */}
                {running ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleCancel();
                    }}
                    disabled={cancelInFlight}
                    className={cn(
                      "w-full flex items-center justify-center gap-1 rounded border px-2 py-1 text-xs transition-colors",
                      "border-amber-300 dark:border-amber-700",
                      "text-amber-700 dark:text-amber-300",
                      cancelInFlight
                        ? "opacity-60 cursor-not-allowed"
                        : "hover:bg-amber-50 dark:hover:bg-amber-950/40"
                    )}
                    aria-label={
                      cancelInFlight
                        ? "Cancel requested — finishing current wave"
                        : "Cancel autonomous run"
                    }
                  >
                    {cancelInFlight ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Ban className="h-3 w-3" />
                    )}
                    {cancelInFlight ? "Cancelling…" : "Cancel run"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissRun();
                    }}
                    className="w-full flex items-center justify-center gap-1 rounded border border-slate-200 dark:border-slate-700 px-2 py-1 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <X className="h-3 w-3" />
                    Dismiss
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
